import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { StatusBadge, type Tom } from "@/components/data/status-badge";
import { useAuth } from "@/lib/auth-context";
import { useOperacaoManejo, useUpdateOperacaoStatus } from "@/hooks/use-manejo";
import { useToast } from "@/hooks/use-toast";
import { mensagemDeErro } from "@/lib/erro-humano";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { podeMudarData, dataNovaValida, dataPrevistaLegivel } from "@shared/manejo-edicao";
import type { MnjOperacaoWithItens, MnjOperacaoEvento, MnjOperacaoStatus } from "@shared/schema";
import { calcularDivergencia, resumirDivergencia } from "@shared/manejo-divergencia";
import { Play, Check, Undo2, X, Send, AlertTriangle, Pencil, CalendarDays } from "lucide-react";
import { usePode } from "@/lib/permissoes";
import { iconeDaCategoria, tituloDaOperacao, areaTotalAplicada } from "./card-operacao";

// Detalhe da operação — spec §6.5. Painel lateral no desktop, folha de baixo no
// celular (o `Sheet` já faz os dois com `side`).
//
// O que esta tela tem que a de criação não tem: a LINHA DO TEMPO. É ela que
// responde "quem fez, quando, e por que isso mudou" — e é o único lugar onde o
// motivo de um cancelamento e a data antiga de um reagendamento ficam legíveis.

const ROTULO_STATUS: Record<MnjOperacaoStatus, string> = {
  backlog: "Backlog",
  planejada: "Planejada",
  em_andamento: "Em andamento",
  concluida: "Concluída",
  cancelada: "Cancelada",
};

const TOM_STATUS: Record<MnjOperacaoStatus, Tom> = {
  backlog: "neutro",
  planejada: "neutro",
  em_andamento: "info",
  concluida: "sucesso",
  cancelada: "erro",
};

const ROTULO_EVENTO: Record<string, string> = {
  criada: "Criada",
  planejada: "Planejada",
  iniciada: "Iniciada",
  pausada: "Pausada",
  retomada: "Retomada",
  concluida: "Concluída",
  cancelada: "Cancelada",
  reagendada: "Reagendada",
  reaberta: "Reaberta",
  comentario: "Comentário",
  editada: "Editada",
};

function quando(v: Date | string): string {
  const d = v instanceof Date ? v : new Date(v);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/**
 * O payload do evento em português — a parte que faz a linha do tempo valer.
 *
 * Sem isto, "Cancelada" não diz por quê e "Editada" não diz o que mudou; a
 * pessoa teria que abrir o DevTools para ler o JSON, que é exatamente o que
 * aconteceu com as pendências do pull no ciclo passado.
 */
function descreverEvento(evento: MnjOperacaoEvento): string | null {
  const p = (evento.payload ?? {}) as Record<string, unknown>;
  if (evento.tipo === "comentario" && typeof p.texto === "string") return p.texto;
  if (typeof p.motivo === "string") return `Motivo: ${p.motivo}`;
  if (p.dataAnterior || p.dataNova) {
    return `Data: ${p.dataAnterior ?? "sem data"} → ${p.dataNova ?? "sem data"}`;
  }
  if (p.divergencia && typeof p.divergencia === "object") {
    const resumo = resumirDivergencia(p.divergencia as any);
    if (resumo) return `Divergência: ${resumo}`;
  }
  return null;
}

export interface OperacaoDetalheProps {
  operacaoId: string | null;
  onOpenChange: (aberto: boolean) => void;
  onIniciar: (op: MnjOperacaoWithItens) => void;
  onConcluir: (op: MnjOperacaoWithItens) => void;
  onReabrir: (op: MnjOperacaoWithItens) => void;
  onCancelar: (op: MnjOperacaoWithItens) => void;
  onComentar: (op: MnjOperacaoWithItens, texto: string) => void;
  /** Abre a tela de EDIÇÃO (a mesma da criação). Sem ela, o botão não aparece. */
  onEditar?: (op: MnjOperacaoWithItens) => void;
  ocupado?: boolean;
}

export function OperacaoDetalhe({
  operacaoId, onOpenChange, onIniciar, onConcluir, onReabrir, onCancelar, onComentar, onEditar, ocupado,
}: OperacaoDetalheProps) {
  const { user } = useAuth();
  // Busca o DETALHE (que traz `eventos`), não reaproveita o item da lista: a
  // lista não carrega a linha do tempo, de propósito.
  const { data: operacao, isLoading } = useOperacaoManejo(operacaoId);
  const [comentario, setComentario] = useState("");

  // Por CAPACIDADE, não por papel: Editar/Cancelar são de quem planeja e Reabrir de quem
  // coordena — o operador (CAMPO) não vê botões que a API recusaria. Comentar fica.
  const podeReabrir = usePode("manejo.reabrir");
  const podePlanejar = usePode("manejo.planejar");
  const podeOperar = usePode("manejo.operar");

  // Mudar a data é do operador também: vai pelo `/status` (operar), não pela edição
  // completa (planejar). Só antes de começar — em andamento a data não muda.
  const { toast } = useToast();
  const mudarStatus = useUpdateOperacaoStatus();
  const [mudandoData, setMudandoData] = useState(false);
  const [novaData, setNovaData] = useState("");
  useEffect(() => { setMudandoData(false); }, [operacaoId]);

  const salvarData = async () => {
    if (!operacao || !dataNovaValida(operacao.dataPrevista, novaData)) return;
    try {
      await mudarStatus.mutateAsync({ id: operacao.id, status: "planejada", dataPrevista: novaData });
      toast({ title: `Data alterada para ${dataPrevistaLegivel(novaData)}` });
      setMudandoData(false);
    } catch (e) {
      const h = mensagemDeErro(e);
      toast({ title: h.titulo, description: h.descricao, variant: "destructive" });
    }
  };

  const enviarComentario = () => {
    if (!operacao || !comentario.trim()) return;
    onComentar(operacao, comentario.trim());
    setComentario("");
  };

  const Icone = operacao ? iconeDaCategoria(operacao.atividade?.categoria) : Play;
  const divergencia = operacao
    ? calcularDivergencia(operacao.itens.map(it => ({
        mnjProdutoId: it.mnjProdutoId,
        produtoNome: it.produto.nome,
        unidade: it.produto.unidade,
        qtdPrevista: Number(it.qtdPrevista),
        qtdRealizada: it.qtdRealizada === null ? null : Number(it.qtdRealizada),
      })))
    : null;

  return (
    <Sheet open={!!operacaoId} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto sm:max-w-lg sm:mx-auto rounded-t-xl">
        {isLoading || !operacao ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Carregando...</p>
        ) : (
          <>
            <SheetHeader className="text-left">
              <SheetTitle className="flex items-start gap-2">
                <Icone className="h-5 w-5 shrink-0 mt-0.5 text-muted-foreground" />
                <span className="min-w-0">{tituloDaOperacao(operacao)}</span>
              </SheetTitle>
            </SheetHeader>

            <div className="space-y-4 py-4">
              <div className="flex flex-wrap items-center gap-1.5">
                <StatusBadge tom={TOM_STATUS[operacao.status]} ponto>
                  {ROTULO_STATUS[operacao.status]}
                </StatusBadge>
                {operacao.prioridade === "alta" && <StatusBadge tom="atencao">prioridade alta</StatusBadge>}
                {operacao.status === "concluida" && divergencia?.divergente && (
                  <StatusBadge tom="atencao">{resumirDivergencia(divergencia)}</StatusBadge>
                )}
              </div>

              {operacao.atividade && <Campo rotulo="Atividade" valor={operacao.atividade.nome} />}
              {operacao.frente && <Campo rotulo="Frente" valor={`${operacao.frente.emoji ? operacao.frente.emoji + " " : ""}${operacao.frente.nome}`} />}
              {operacao.local && <Campo rotulo="Local" valor={operacao.local} />}
              {operacao.setores.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Setores</p>
                  <div className="flex flex-wrap items-center gap-1">
                    {operacao.setores.map(s => (
                      <Badge key={s.setorId} variant="outline" className="font-normal">
                        {s.setor.nome} · {Number(s.areaHaSnapshot).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} ha
                      </Badge>
                    ))}
                    <span className="text-xs text-muted-foreground tabular-nums">
                      total {areaTotalAplicada(operacao).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} ha
                    </span>
                  </div>
                </div>
              )}
              <Campo rotulo="Data prevista" valor={dataPrevistaLegivel(operacao.dataPrevista)} />
              {podeOperar && podeMudarData(operacao.status) && (
                mudandoData ? (
                  <div className="space-y-2 rounded-md border p-3" data-testid="mudar-data-form">
                    <Input
                      type="date"
                      className="min-h-11 text-base"
                      value={novaData}
                      min={hojeNaFazenda()}
                      onChange={e => setNovaData(e.target.value)}
                      data-testid="input-nova-data"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="outline" className="min-h-11" disabled={mudarStatus.isPending} onClick={() => setMudandoData(false)}>
                        Cancelar
                      </Button>
                      <Button
                        className="min-h-11"
                        disabled={mudarStatus.isPending || !dataNovaValida(operacao.dataPrevista, novaData)}
                        onClick={() => void salvarData()}
                        data-testid="btn-salvar-data"
                      >
                        Salvar data
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="min-h-11"
                    disabled={ocupado}
                    onClick={() => { setNovaData(operacao.dataPrevista ?? hojeNaFazenda()); setMudandoData(true); }}
                    data-testid="btn-mudar-data"
                  >
                    <CalendarDays className="h-4 w-4 mr-1.5" />Mudar data
                  </Button>
                )
              )}
              {operacao.volumeCaldaL && (
                <Campo rotulo="Calda" valor={`${Number(operacao.volumeCaldaL).toLocaleString("pt-BR")} L`} />
              )}
              {operacao.executores.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Pessoas</p>
                  <div className="flex flex-wrap gap-1">
                    {operacao.executores.map(e => (
                      <Badge key={e.id} variant="outline" className="font-normal">
                        {e.funcionario.apelido ?? e.funcionario.nome}
                        <span className="ml-1 text-muted-foreground">
                          {e.papel === "executou" ? "executou" : "atribuído"}
                        </span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {operacao.itens.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Insumos</p>
                  <div className="rounded-md border divide-y">
                    {operacao.itens.map(it => (
                      <div key={it.id} className="flex items-center justify-between gap-2 px-2.5 py-2 text-sm">
                        <span className="min-w-0">
                          <span className="block truncate">{it.produto.nome}</span>
                          <span className="block text-xs text-muted-foreground">{it.produto.unidade}</span>
                        </span>
                        <span className="shrink-0 text-right tabular-nums">
                          <span className="block text-xs text-muted-foreground">
                            previsto {Number(it.qtdPrevista).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}
                          </span>
                          <span className="block">
                            {it.qtdRealizada === null
                              ? "—"
                              : Number(it.qtdRealizada).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {operacao.observacao && <Campo rotulo="Observação" valor={operacao.observacao} />}
              {operacao.observacaoConclusao && (
                <Campo rotulo="Observação da conclusão" valor={operacao.observacaoConclusao} />
              )}

              {/* Ações de topo por estado (spec §6.5) — o mesmo conjunto que o
                  card oferece, mais cancelar e reabrir, que não cabem no card. */}
              <div className="flex flex-wrap gap-1.5 border-t pt-4">
                {onEditar && (
                  <Button variant="outline" className="min-h-11" disabled={ocupado} onClick={() => onEditar(operacao)} data-testid="btn-editar-operacao">
                    <Pencil className="h-4 w-4 mr-1.5" />Editar
                  </Button>
                )}
                {(operacao.status === "planejada" || operacao.status === "backlog") && (
                  <Button
                    className="flex-1 min-h-11 bg-sucesso text-white hover:bg-sucesso/90"
                    disabled={ocupado}
                    onClick={() => onIniciar(operacao)}
                  >
                    <Play className="h-4 w-4 mr-1.5" />Iniciar
                  </Button>
                )}
                {(operacao.status === "em_andamento" || operacao.status === "planejada") && (
                  <Button
                    className="flex-1 min-h-11 bg-info text-white hover:bg-info/90"
                    disabled={ocupado}
                    onClick={() => onConcluir(operacao)}
                  >
                    <Check className="h-4 w-4 mr-1.5" />Concluir
                  </Button>
                )}
                {operacao.status === "concluida" && podeReabrir && (
                  <Button variant="outline" className="min-h-11" disabled={ocupado} onClick={() => onReabrir(operacao)}>
                    <Undo2 className="h-4 w-4 mr-1.5" />Reabrir
                  </Button>
                )}
                {podePlanejar && operacao.status !== "concluida" && operacao.status !== "cancelada" && (
                  <Button
                    variant="ghost"
                    className="min-h-11 text-erro hover:text-erro"
                    disabled={ocupado}
                    onClick={() => onCancelar(operacao)}
                  >
                    <X className="h-4 w-4 mr-1.5" />Cancelar
                  </Button>
                )}
              </div>

              {/* ─── Linha do tempo ─────────────────────────────────────────── */}
              <div className="space-y-2 border-t pt-4">
                <p className="text-xs font-medium text-muted-foreground">Linha do tempo</p>
                <ol className="space-y-2.5">
                  {(operacao.eventos ?? []).map(ev => {
                    const detalhe = descreverEvento(ev);
                    return (
                      <li key={ev.id} className="flex gap-2.5 text-sm">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
                        <span className="min-w-0">
                          <span className="font-medium">{ROTULO_EVENTO[ev.tipo] ?? ev.tipo}</span>
                          <span className="text-muted-foreground"> · {quando(ev.createdAt)}</span>
                          {ev.origem !== "app" && (
                            <Badge variant="outline" className="ml-1.5 font-normal">{ev.origem}</Badge>
                          )}
                          {detalhe && <span className="block text-muted-foreground">{detalhe}</span>}
                        </span>
                      </li>
                    );
                  })}
                  {(operacao.eventos ?? []).length === 0 && (
                    <li className="text-sm text-muted-foreground">Nenhum evento registrado.</li>
                  )}
                </ol>
              </div>

              {/* Comentário — o caminho de correção depois dos 7 dias de reabertura. */}
              <div className="flex gap-1.5 border-t pt-4">
                <Input
                  placeholder="Comentar..."
                  className="min-h-11"
                  value={comentario}
                  onChange={e => setComentario(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") enviarComentario(); }}
                  data-testid="input-comentario"
                />
                <Button
                  variant="outline"
                  className="min-h-11 shrink-0"
                  disabled={!comentario.trim() || ocupado}
                  onClick={enviarComentario}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>

              {operacao.status === "concluida" && !podeReabrir && (
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                  Concluída. Para corrigir, peça a um administrador ou encarregado para reabrir —
                  ou registre a correção como comentário.
                </p>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-medium text-muted-foreground">{rotulo}</p>
      <p className="text-sm">{valor}</p>
    </div>
  );
}
